import { Component, Input } from "@angular/core";
export interface DeliveryCounts {
  total: number;
  delivered: number;
  failed: number;
  expired: number;
  unconfirmed: number;
}
@Component({
  selector: "sms-delivery-summary",
  template: `<section class="summary">
    <header>
      <h3>Resumen de estados de entrega</h3>
      <p>{{ description }}</p>
    </header>
    <div class="cards">
      @for (item of items; track item.key) {
        <button
          type="button"
          [class]="item.key"
          [attr.aria-expanded]="open === item.key"
          (click)="open = open === item.key ? null : item.key"
        >
          <div class="title">
            <strong>{{ item.label }}</strong
            ><span
              >{{ counts ? percent(item.key).toFixed(1) + "%" : "—" }} ⓘ</span
            >
          </div>
          <b>{{ counts ? counts[item.key] : "—" }}</b>
          <div class="track">
            <div [style.width.%]="percent(item.key)"></div>
          </div>
          @if (open === item.key) {
            <dl>
              <dt>Estado</dt>
              <dd>{{ item.code }}</dd>
              <dt>Descripción</dt>
              <dd>{{ item.description }}</dd>
              <dt>Significado</dt>
              <dd>{{ item.meaning }}</dd>
            </dl>
          }
          <small
            >{{
              open === item.key ? "Ocultar información" : "Ver información"
            }}
            ⌄</small
          >
        </button>
      }
    </div>
  </section>`,
  styles: [
    `
      :host {
        display: block;
        margin: 20px 0;
      }
      .summary {
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        background: white;
        padding: 18px;
      }
      header {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
      }
      h3 {
        font-size: 14px;
        color: #0f172a;
        margin: 0 0 14px;
      }
      header p {
        font-size: 12px;
        color: #64748b;
        margin: 0;
      }
      .cards {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
      }
      button {
        padding: 16px;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        background: #fff;
        text-align: left;
        font: inherit;
        cursor: pointer;
        color: #0f172a;
      }
      .title {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-size: 13px;
      }
      .title span,
      small {
        color: #64748b;
        font-size: 11px;
      }
      b {
        display: block;
        font-size: 26px;
        margin: 12px 0;
      }
      .track {
        height: 6px;
        background: #f1f5f9;
        border-radius: 5px;
        overflow: hidden;
        margin-bottom: 12px;
      }
      .track div {
        height: 100%;
        background: currentColor;
      }
      .delivered .title strong,
      .delivered .track {
        color: #059669;
      }
      .failed .title strong,
      .failed .track {
        color: #dc2626;
      }
      .expired .title strong,
      .expired .track {
        color: #d97706;
      }
      .unconfirmed .title strong,
      .unconfirmed .track {
        color: #2563eb;
      }
      button[aria-expanded="true"] {
        background: #f8fafc;
      }
      dl {
        font-size: 12px;
        background: white;
        padding: 12px;
        border-radius: 8px;
      }
      dt {
        color: #64748b;
        margin-top: 8px;
      }
      dd {
        margin: 4px 0;
        line-height: 1.5;
      }
      button:focus-visible {
        outline: 2px solid #60a5fa;
      }
      @media (max-width: 640px) {
        .cards {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class DeliveryStatusSummaryComponent {
  @Input() counts: DeliveryCounts | null = null;
  @Input() description = "";
  open: string | null = null;
  readonly items = [
    {
      key: "delivered" as const,
      label: "Entregados",
      code: "DELIVERED",
      description: "Mensaje entregado exitosamente",
      meaning: "El mensaje llegó al dispositivo del destinatario",
    },
    {
      key: "failed" as const,
      label: "Fallidos",
      code: "FAILED",
      description: "Mensaje falló en la entrega",
      meaning: "No se pudo entregar por algún motivo",
    },
    {
      key: "expired" as const,
      label: "Expirados",
      code: "EXPIRED",
      description: "Mensaje expiró por timeout",
      meaning: "Se alcanzó el tiempo límite de entrega configurado",
    },
    {
      key: "unconfirmed" as const,
      label: "Sin confirmar",
      code: "SIN CONFIRMAR",
      description: "El proveedor aún no confirma el resultado",
      meaning: "El mensaje está pendiente de un estado final de entrega",
    },
  ];
  percent(key: "delivered" | "failed" | "expired" | "unconfirmed") {
    return this.counts?.total
      ? Math.min(100, (this.counts[key] / this.counts.total) * 100)
      : 0;
  }
}
